import type { Outputs } from "@postdaily/api/routers/index";

import { joinWithAnd } from "./join-with-and";

/**
 * Plan prices as public copy — the pricing FAQ answer and the `Offer` JSON-LD.
 *
 * Everything here is computed from the live Polar catalog (`billing.listPlans`)
 * the pricing route's loader reads while prerendering, so no price is written
 * into the source. Keep it that way: `comparison-data.ts` and the pricing page
 * explain why a stored price is the one number that goes stale unnoticed.
 */

/** One plan from the live catalog, Free first, then the Polar plans by rank. */
export type CatalogPlan = Outputs["billing"]["listPlans"][number];

/** One billing interval's price on a plan. Amounts are in minor units. */
export type CatalogPrice = NonNullable<CatalogPlan["monthly"]>;

/** Paid plans that carry at least one price, in the catalog's rank order. */
function paidPlans(plans: readonly CatalogPlan[]): CatalogPlan[] {
  return plans.filter(
    (plan) =>
      plan.planGroup !== "free" &&
      (plan.monthly !== null || plan.annual !== null),
  );
}

/**
 * False when the catalog holds only the Free plan — which is what
 * `listPlans` returns when Polar cannot be reached — so callers publish no
 * price at all rather than a pricing answer with the paid plans missing.
 */
export function hasPaidPrices(plans: readonly CatalogPlan[]): boolean {
  return paidPlans(plans).length > 0;
}

/**
 * `$9`, or `$9.50` when there are cents. The currency travels with the amount
 * from Polar, so nothing here assumes dollars.
 */
export function formatPlanPrice(price: CatalogPrice): string {
  const hasCents = price.amount % 100 !== 0;

  return new Intl.NumberFormat("en-US", {
    currency: price.currency.toUpperCase(),
    maximumFractionDigits: hasCents ? 2 : 0,
    minimumFractionDigits: hasCents ? 2 : 0,
    style: "currency",
  }).format(price.amount / 100);
}

function planPriceClause(plan: CatalogPlan): string {
  const parts: string[] = [];

  if (plan.monthly) {
    parts.push(`${formatPlanPrice(plan.monthly)} a month`);
  }

  if (plan.annual) {
    parts.push(`${formatPlanPrice(plan.annual)} a year`);
  }

  return `${plan.displayName} is ${parts.join(" or ")}`;
}

/**
 * One plain-language answer to "how much does PostDaily cost?", every figure
 * taken from the catalog. Written as whole sentences because this is the text
 * an answer engine lifts verbatim, and a bare price table does not survive
 * being quoted out of the page.
 *
 * `null` when the catalog carries no paid prices, for the reason given on
 * `hasPaidPrices`.
 */
export function planPriceSummary(plans: readonly CatalogPlan[]): string | null {
  const paid = paidPlans(plans);

  if (paid.length === 0) {
    return null;
  }

  const sentences: string[] = [];
  const free = plans.find((plan) => plan.planGroup === "free");

  if (free) {
    sentences.push(
      `The ${free.displayName} plan costs nothing and needs no card.`,
    );
  }

  sentences.push(`${joinWithAnd(paid.map(planPriceClause))}.`);

  // Stated only when every paid plan's annual price carries the same trial:
  // trial length is set per Polar product, and a sentence claiming "every
  // annual plan" would be wrong the moment one of them differs.
  const trialLengths = new Set(paid.map((plan) => plan.annual?.trialDays ?? 0));
  const [trialDays] = trialLengths;

  if (trialLengths.size === 1 && trialDays !== undefined && trialDays > 0) {
    sentences.push(
      `Every annual plan starts with a ${trialDays}-day free trial.`,
    );
  }

  const currencies = new Set(
    paid.flatMap((plan) =>
      [plan.monthly, plan.annual].flatMap((price) =>
        price ? [price.currency.toUpperCase()] : [],
      ),
    ),
  );
  const [currency] = currencies;

  if (currencies.size === 1 && currency !== undefined) {
    sentences.push(`Prices are in ${currency}.`);
  }

  return sentences.join(" ");
}

/**
 * The pricing page's title and description with the lowest paid monthly
 * price in them, e.g. "PostDaily Pricing — Free Plan, Paid Plans from
 * $9/month". The generic "Pricing — PostDaily" told a searcher nothing, and
 * the price is the one fact they came for. `null` without paid prices, so the
 * route falls back to `PAGE_META.pricing` rather than inventing a figure.
 */
export function pricingHeadMeta(
  plans: readonly CatalogPlan[] | null | undefined,
): { description: string; title: string } | null {
  const monthly = paidPlans(plans ?? []).flatMap((plan) =>
    plan.monthly ? [plan.monthly] : [],
  );

  if (monthly.length === 0) {
    return null;
  }

  const lowest = monthly.reduce((min, price) =>
    price.amount < min.amount ? price : min,
  );
  const from = formatPlanPrice(lowest);

  return {
    description: `A free plan, and paid plans from ${from} a month with a channel allowance instead of per-channel billing. Compare every plan's limits.`,
    title: `PostDaily Pricing — Free Plan, Paid Plans from ${from}/month`,
  };
}
